import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import type { ModalState, PositionForm, Position } from '../types/system-parameters.types';

interface PositionModalProps {
  modalState: ModalState;
  onClose: () => void;
  onSave: (formData: PositionForm, isEdit?: boolean, itemId?: string) => void;
}

export const PositionModal: React.FC<PositionModalProps> = ({
  modalState,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<PositionForm>({
    name: '',
    code: '',
    level: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);

  // Resetear formulario cuando se abra en modo crear o cuando cambie el item
  useEffect(() => {
    if (modalState.isOpen) {
      if (modalState.mode === 'edit' && modalState.item) {
        const position = modalState.item as Position;
        setFormData({
          name: position.additionalData.name,
          code: position.additionalData.code,
          level: position.additionalData.level,
          description: position.additionalData.description
        });
      } else {
        setFormData({
          name: '',
          code: '',
          level: '',
          description: ''
        });
      }
    }
  }, [modalState.isOpen, modalState.mode, modalState.item]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.code.trim()) {
      return;
    }

    setLoading(true);
    try {
      // Simular delay de red
      await new Promise(resolve => setTimeout(resolve, 800));

      onSave(
        formData,
        modalState.mode === 'edit',
        modalState.item?.id
      );
      onClose();
    } catch (error) {
      console.error('Error al guardar posición:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeGeneration = () => {
    if (formData.name) {
      const code = formData.name
        .toUpperCase()
        .replace(/[^A-Z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 20);
      setFormData(prev => ({ ...prev, code }));
    }
  };

  return (
    <Dialog open={modalState.isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {modalState.mode === 'edit' ? 'Editar Posición' : 'Nueva Posición'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nombre *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, name: e.target.value }));
              }}
              onBlur={handleCodeGeneration}
              placeholder="Ej: Administrador del Sistema"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="code">Código *</Label>
            <Input
              id="code"
              value={formData.code}
              onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
              placeholder="Ej: ADMIN_SISTEMA"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Descripción de la posición"
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!formData.name.trim() || !formData.code.trim() || loading}
              className="bg-macartur-primary"
            >
              {loading ? 'Guardando...' : modalState.mode === 'edit' ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};